import { Linear, gsap } from "gsap";
import * as PIXI from "pixi.js";
import { DestroyUtil } from "../../util/view/DestroyUtil";
import { AbstractControl } from "./AbstractControl";
import { ControlConstants } from "./constant/ControlConstants";
import { ViewUtils } from "../../util/view/ViewUtils";
/**
 * CountingTextField.ts
 */
export class CountingTextField extends AbstractControl {
    constructor(properties, portraitProperties, formatter) {
        super(properties, portraitProperties);
        this.formatter = formatter;
    }
    create() {
        super.create();
        this.tweenDuration = this.uiProperties.tweenDuration;
        this.createTextField();
        this.resetPosition();
        this.setPivot();
    }
    destroy() {
        super.destroy();
        DestroyUtil.destroy(this.textField);
        this.landscapeProperties = null;
        this.portraitProperties = null;
        this.uiProperties = null;
        this.textField = null;
        this.formatter = null;
        this.valueTween = null;
        this.scaleUpTween = null;
        this.scaleDownTween = null;
    }
    resetPosition() {
        this.position.set(0, 0);
    }
    createProperties(landscapeProperties, portraitProperties) {
        super.createProperties(landscapeProperties, portraitProperties);
        this.landscapeProperties = landscapeProperties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    static createCountingField(parent, properties, formatter) {
        let field;
        if (properties) {
            field = new CountingTextField(properties, formatter ? formatter.bind(this) : null);
            parent.addChild(field);
        }
        return field;
    }
    createTextField() {
        this.textField = ViewUtils.createField(this, this.landscapeProperties, this.portraitProperties);
    }
    setPivot() {
        if (this.uiProperties.scaleValue) {
            let align = this.uiProperties.horizontalAlign;
            let width = Math.round(this.textField.getWidth() / 2);
            let height = Math.round(this.textField.getHeight() / 2);
            let pivot;
            if (align == ControlConstants.RIGHT) {
                pivot = new PIXI.Point(0, height);
            }
            else if (align == ControlConstants.CENTER) {
                pivot = new PIXI.Point(width, height);
            }
            else if (align == ControlConstants.LEFT) {
                pivot = new PIXI.Point(0, height);
            }
            this.textField.pivot.copyFrom(pivot);
        }
    }
    startTween(value) {
        this.value = value;
        this.valueTween = gsap.to(this, {
            displayValue: this.value,
            duration: this.tweenDuration,
            onUpdate: this.handleValueTweenUpdate,
            callbackScope: this
        });
        if (this.uiProperties.scaleValue) {
            this.scaleUpTween = gsap.to(this.textField.scale, { x: 1.4, y: 1.4, ease: Linear.easeIn, duration: 1 });
            this.scaleDownTween = gsap.to(this.textField.scale, {
                x: 1,
                y: 1,
                delay: 0.5,
                duration: 0.5,
                ease: Linear.easeOut
            });
        }
    }
    handleValueTweenUpdate() {
        let value = this.formatter ? this.formatter.call(this, this.displayValue) : this.displayValue.toString();
        this.setText(value);
    }
    setValue(value, skipTween = false) {
        if (value != this.value) {
            this.clearTween();
            let tweenOnDecrement = (value < this.value) && !this.uiProperties.tweenOnDecrement;
            if (skipTween || isNaN(this.value) || tweenOnDecrement) {
                this.value = this.displayValue = value;
                this.handleValueTweenUpdate();
            }
            else {
                this.startTween(value);
            }
        }
    }
    clearTween() {
        if (this.valueTween && this.valueTween.isActive()) {
            this.valueTween.kill();
            this.handleValueTweenUpdate();
        }
        this.valueTween = null;
    }
    clear() {
        this.clearTween();
        this.textField.clear();
        this.value = NaN;
        this.displayValue = NaN;
    }
    setText(value) {
        this.textField.setText(value);
    }
    setLangKey(value) {
        this.textField.setLangKey(value);
    }
    setTweenDuration(value) {
        this.tweenDuration = value;
    }
    getText() {
        return this.textField.getText();
    }
    getTextField() {
        return this.textField;
    }
    getValue() {
        return this.value;
    }
    getDisplayValue() {
        return this.value;
    }
    handleRotate(event) {
        // Do Nothing will be handled by TextField
    }
}
//# sourceMappingURL=CountingTextField.js.map